import { createContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../languages.json';

export const LanguageContext = createContext();

// The site's languages, from src/languages.json. Pages in the default one
// have no language in their path (/eufyCam-S4); the others' start with it
// (/en/eufyCam-S4).
export const LANGUAGES = Object.keys(config.languages);
export const DEFAULT_LANGUAGE = config.default;

// The visitor's language: the one they first opened the site in, until they
// pick another with the language switch. public/index.html remembers the first
// and opens every page in this language; keep its logic in step with these.
export const LANGUAGE_KEY = 'language';

export const languageFromPath = (pathname) => {
    const first = pathname.split('/').filter(segment => segment !== '')[0];
    return first !== DEFAULT_LANGUAGE && LANGUAGES.includes(first) ? first : DEFAULT_LANGUAGE;
};

// The same page in another language: "/en/eufyCam-S4" in the default
// language is "/eufyCam-S4".
export const pathInLanguage = (pathname, language) => {
    const segments = pathname.split('/').filter(segment => segment !== '');
    if (languageFromPath(pathname) !== DEFAULT_LANGUAGE) segments.shift();
    if (language !== DEFAULT_LANGUAGE) segments.unshift(language);
    return `/${segments.join('/')}`;
};

export const LanguageProvider = ({ children }) => {
    const location = useLocation();
    // Known from the first render, so the page drawn at build time is in the
    // page's own language, not the default with the text filled in later.
    const [language, setLanguage] = useState(() => languageFromPath(location.pathname));
    const { i18n } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!language) return
        i18n.changeLanguage(language);
    }, [language]);

    useEffect(() => {
        // Follow the URL when navigating between languages
        setLanguage(languageFromPath(location.pathname));
    }, [location.pathname]);

    const changeLanguage = (newLanguage) => {
        setLanguage(newLanguage);
        try {
            localStorage.setItem(LANGUAGE_KEY, newLanguage);
        } catch {
            // Without storage the choice still holds while browsing, until the next visit.
        }
        navigate(pathInLanguage(location.pathname, newLanguage), { replace: true });
    };

    return (
        <LanguageContext.Provider value={{ language, changeLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};
